import { inject, injectable, injectFromBase } from 'inversify'
import ReopenAccount from '@/data/models/ReopenAccount'
import { RepoSettings } from '@/decorators/repositoryDecorators'
import OrmLocalStorageRepository from '@/data/repositories/ormLocalStorageRepository'
import { SERVICE_TYPES } from '@/core/container/types'
import type { HttpService } from '@/services/transport/httpService'
import type { AbstractEndpointsInterface } from '@/services/endpointsService'
import type LocalStorageTransport from '@/services/transport/storage/localStorageTransport'
import type LocalStorageAdapter from '@/services/transport/storage/localStorageAdapter'
import type LoggerService from '@/services/loggerService'

@RepoSettings({
  model: ReopenAccount,
  fetch: ReopenAccount.entity,
  create: ReopenAccount.entity,
  single: ReopenAccount.entity,
})
@injectable()
@injectFromBase()
export default class ReopenAccountRepository extends OrmLocalStorageRepository<ReopenAccount> {
  protected id = '1'

  constructor(
    @inject(SERVICE_TYPES.LocalStorageTransport) protected readonly transport: LocalStorageTransport,
    @inject(SERVICE_TYPES.LocalStorageAdapter) protected readonly localStorageAdapter: LocalStorageAdapter,
    @inject(SERVICE_TYPES.LoggerService) protected readonly loggerService: LoggerService,
    @inject(SERVICE_TYPES.Api) protected readonly api: HttpService,
    @inject(SERVICE_TYPES.EndpointsService) protected readonly endpointsService: AbstractEndpointsInterface,
  ) {
    super(transport, localStorageAdapter, loggerService)
  }

  public reopenAccount(username: string, password: string) {
    return this.api.post(this.endpointsService.getPath('reopenAccount'), { username, password })
  }

  public async fetchReopenAccountData(): Promise<ReopenAccount | null> {
    const data = await this.getRequest(this.id)

    if (!data) {
      return null
    }

    await this.storeReopenAccountData(data)
    return this.getReopenAccountData()
  }

  public getReopenAccountData(): ReopenAccount | null {
    return this.findEntityByIdOrNull(this.id)
  }

  public async storeReopenAccountData(reopenAccountData: Pick<ReopenAccount, 'loginOrEmail' | 'fullName'>) {
    await this.postRequest({
      ...reopenAccountData,
      id: this.id,
    })
    await this.fill(this.id)
  }

  public clearReopenAccountData() {
    return this.deleteRequest(this.id)
  }
}
